import React, { useEffect } from 'react'
import './MessagingStyle.css'
import useMessaging from './useMessaging'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useCommunicationsLeaveGuard from '../shared/useCommunicationsLeaveGuard'
import { IMessagingSettings } from '@dataTypes/messaging-settings.type'
import {
  isSettingFieldChanged,
  settingsFieldClassName,
} from '@utils/settings.util'
import {
  Accordion,
  AccordionItem,
  Button,
  Select,
  ToggleSwitch,
} from '@enamelai/design-system'

type FeatureToggleKey = Exclude<
  keyof IMessagingSettings,
  | 'default_sms_clinic_number_id'
  | 'auto_reply_during_hours_message'
  | 'auto_reply_after_hours_message'
  | 'auto_reply_during_hours_enabled'
  | 'auto_reply_after_hours_enabled'
>

const FEATURE_TOGGLES: {
  key: FeatureToggleKey
  title: string
  description: string
}[] = [
  {
    key: 'enable_sms',
    title: 'Enable SMS messaging',
    description: 'Master switch for all outbound SMS.',
  },
  {
    key: 'send_appointment_reminders',
    title: 'Send appointment reminders',
    description: 'Automated texts before patient appointments.',
  },
  {
    key: 'track_message_status',
    title: 'Track message status',
    description: 'Log delivery receipts.',
  },
]

const selectString = (value: string | string[]): string =>
  typeof value === 'string' ? value : (value[0] ?? '')

const Messaging = (): React.ReactElement => {
  const hooks = useMessaging()
  const leaveGuard = useCommunicationsLeaveGuard(
    hooks.hasChanges && hooks.dataReady,
  )

  useEffect(() => {
    hooks.setHeaderButtons(
      <>
        <Button
          size="sm"
          variant="secondary"
          disabled={!hooks.hasChanges || hooks.loading || !hooks.dataReady}
          onClick={() => leaveGuard.requestLeave(hooks.handleReset)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={!hooks.hasChanges || hooks.loading || !hooks.dataReady}
          onClick={() => {
            void hooks.handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )

    return () => {
      hooks.setHeaderButtons(null)
    }
  }, [
    hooks.dataReady,
    hooks.handleReset,
    hooks.handleSave,
    hooks.hasChanges,
    hooks.loading,
    hooks.setHeaderButtons,
    leaveGuard.requestLeave,
  ])

  return (
    <div className="scp-messaging-container" id="settings-messaging">
      {leaveGuard.modal}
      {!hooks.dataReady ? <SettingsSkeleton variant="form" /> : null}
      {hooks.dataReady ? (
        <>
          <div className="scp-msg-number-field">
            <Select
              label="Default SMS clinic number"
              showTooltip
              className={settingsFieldClassName(
                isSettingFieldChanged(
                  hooks.data.default_sms_clinic_number_id ?? '',
                  hooks.savedBaseline.default_sms_clinic_number_id ?? '',
                ),
              )}
              value={hooks.data.default_sms_clinic_number_id ?? ''}
              disabled={hooks.numberOptions.length === 0 || hooks.loading}
              placeholder="Select a clinic number"
              options={hooks.numberOptions.map((option) => ({
                label: `${option.name} (${option.phone_number})`,
                value: option.clinic_number_id,
              }))}
              onChange={(value) =>
                hooks.handleNumberChange(selectString(value))
              }
            />
          </div>

          <Accordion
            className="scp-msg-accordion"
            allowMultiple
            defaultOpenIds={['messaging-features']}
          >
            <AccordionItem
              id="messaging-features"
              title="Messaging features"
              showInfo
            >
              <div className="scp-msg-feature-list">
                {FEATURE_TOGGLES.map((feature) => (
                  <div key={feature.key} className="scp-msg-feature-row">
                    <div className="scp-msg-feature-copy">
                      <span className="scp-msg-feature-title">
                        {feature.title}
                      </span>
                      <span className="scp-msg-feature-description">
                        {feature.description}
                      </span>
                    </div>
                    <ToggleSwitch
                      id={`messaging-${feature.key}`}
                      checked={hooks.data[feature.key] as boolean}
                      aria-label={feature.title}
                      onChange={(checked) => {
                        hooks.handleChange(feature.key, checked)
                      }}
                    />
                  </div>
                ))}
              </div>
            </AccordionItem>
          </Accordion>
        </>
      ) : null}
    </div>
  )
}

export default Messaging
